import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Section for Utilities and Context
import "./peopleFeed.css";
import API from "../utils/API";

// Section for Components
import defaultPortrait from "../assets/portrait-5.png";

export default function PeopleFeed(props) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false); 
    const [people, setPeople] = useState([]);
    const [titleList, setTitleList] = useState([]);
    const [officeList, setOfficeList] = useState([]);
    const [locationList, setLocationList] = useState([]);
    const [titleFilterSelect, setTitleFilterSelect] = useState(undefined);
    const [officeFilterSelect, setOfficeFilterSelect] = useState(undefined);
    const [locationFilterSelect, setLocationFilterSelect] = useState(undefined);    

    useEffect(() => {
        const fetchPeople = async () => {
            setLoading(true);
            try {
                const response = props.division_id
                    ? await API.getDivisionColleagues({ division_id: props.division_id })
                    : await API.getOrganizationColleagues({ organization_id: props.organization_id });

                setPeople(response.data);

                const titles = [...new Set(response.data.map(p => p.title).filter(Boolean))]
                    .sort((a, b) => a.localeCompare(b));
                setTitleList(titles);

                // Only populate office filter when showing a full org (not a single division)
                if (!props.division_id) {
                    const offices = [...new Set(response.data.map(p => p.Division?.division_name).filter(Boolean))]
                        .sort((a, b) => a.localeCompare(b));
                    setOfficeList(offices);
                }

                const rawLocations = response.data.flatMap(p => {
                    const locations = [];
                    if (p.city && p.state) locations.push({ city: p.city, state: p.state });
                    if (p.Division?.city && p.Division?.state) locations.push({ city: p.Division.city, state: p.Division.state });
                    return locations;
                });

                const locationKeySet = new Set();
                const uniqueLocations = [];
                for (const loc of rawLocations) {
                    const key = `${loc.city.toLowerCase()}-${loc.state.toLowerCase()}`;
                    if (!locationKeySet.has(key)) { locationKeySet.add(key); uniqueLocations.push(loc); }
                }
                uniqueLocations.sort((a, b) => {
                    const c = a.city.localeCompare(b.city);
                    return c !== 0 ? c : a.state.localeCompare(b.state);
                });
                setLocationList(uniqueLocations);
                setLoading(false);
            } catch (error) {
                console.log("Error Fetching People Data:", error);
            }
        };

        if (props.division_id || props.organization_id) {
            fetchPeople();
        }

    }, [props.organization_id, props.division_id]);

    const handleNavigate = (people_id) => {
        navigate("/profile/" + people_id);
    }


    const filterPeople = people.filter(person => {

        const titleFilter = titleFilterSelect === undefined || person.title === titleFilterSelect;

        const officeFilter = officeFilterSelect === undefined || person.Division?.division_name === officeFilterSelect;

        const locationFilter = locationFilterSelect === undefined || 
            `${person.city}, ${person.state}` === locationFilterSelect || 
            `${person.Division?.city}, ${person.Division?.state}` === locationFilterSelect;
      

        return titleFilter & officeFilter && locationFilter;
    });

    
    const handlePeopleList = (people) => {
        if (people.length === 0 && !loading) {
            return <div className="py-2 ms-3">No people to show.</div>;
        }

        // Filter to ensure unique people by profile_id
        const uniquePeople = people
        .filter((person, index, self) =>
            index === self.findIndex(p => p.profile_id === person.profile_id)
        )
        .filter(person => person.profile_id !== props.profile_id); // Exclude matching profile_id


        const peopleList = uniquePeople.map((person) => (
            <div key={person.profile_id}
                className="people-card"
                onClick={() => handleNavigate(person.ProfileInfo.profile_id)}
            >
                <img src={person.ProfileInfo.profile_picture || defaultPortrait}
                    className="people-card-picture"
                    alt={`${person.ProfileInfo.first_name} ${person.ProfileInfo.last_name}`}
                />

                <div className="people-card-details">
                    <span className="people-card-name">{person.ProfileInfo.first_name} {person.ProfileInfo.last_name}</span>
                    {person.title && (
                        <span className="people-card-title">{person.title}</span>
                    )}
                    {person.Division?.division_name && (
                        <span className="people-card-division">{person.Division.division_name}</span>
                    )}
                    {person.ProfileInfo.work_email && (
                        <a href={"mailto:" + person.ProfileInfo.work_email}
                            className="people-card-email"
                            onClick={(e) => e.stopPropagation()}
                        >{person.ProfileInfo.work_email}</a>
                    )}
                </div>
            </div>
        ));

        return peopleList.sort(() => Math.random() - 0.5);

    }


    const handleResetFilters = () => {
        setTitleFilterSelect(undefined);
        setOfficeFilterSelect(undefined);
        setLocationFilterSelect(undefined);
    };

    const hasFilters = titleList.length > 0 || officeList.length >= 2 || locationList.length > 0;

    return (
        <>
            <div className="org-people-feed mt-sm-0 mt-0 pt-0" >
                {hasFilters && (
                    <div className="people-filter-bar">
                        {titleList.length > 0 && (
                            <select
                                className="form-select people-type-filter-select"
                                value={titleFilterSelect !== undefined ? titleFilterSelect : ""}
                                onChange={(e) => setTitleFilterSelect(e.target.value)}
                            >
                                <option value="" disabled>Title</option>
                                {titleList.map(title => (
                                    <option key={title} value={title}>{title}</option>
                                ))}
                            </select>
                        )}

                        {officeList.length >= 2 && (
                            <select
                                className="form-select people-type-filter-select"
                                value={officeFilterSelect !== undefined ? officeFilterSelect : ""}
                                onChange={(e) => setOfficeFilterSelect(e.target.value)}
                            >
                                <option value="" disabled>Office</option>
                                {officeList.map(office => (
                                    <option key={office} value={office}>{office}</option>
                                ))}
                            </select>
                        )}
                        {locationList.length > 0 && (
                            <select
                                className="form-select people-type-filter-select"
                                value={locationFilterSelect !== undefined ? locationFilterSelect : ""}
                                onChange={(e) => setLocationFilterSelect(e.target.value)}
                            >
                                <option value="" disabled>Location</option>
                                {locationList.map(loc => (
                                    <option key={loc.city} value={`${loc.city}, ${loc.state}`}>{loc.city}, {loc.state}</option>
                                ))}
                            </select>
                        )}

                        <button
                            className="clear-people-filter-button"
                            onClick={handleResetFilters}
                        >Reset</button>
                    </div>
                )}

                <div className="people-card-grid">
                    {handlePeopleList(filterPeople)}
                </div>

                {loading && people.length === 0 && (
                    <div className="d-flex position-absolute top-50 start-50 translate-middle ">
                        <div className="spinner-border loading-spinner" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                )}

            </div>

        </>
    )
}